import Button from "@ui/button/Button"
import { useEffect, useRef, useState } from "react"
import { toast } from "react-toastify"
import { systemErrorMessage, systemPost } from "src/services/superAdminApi"
import { formatMinorCurrency } from "src/utils/systemBalance"
import { AdminModal } from "./AdminModal"
import { T } from "../adminTheme"
import { Callout, Disclosure, Fact, Facts, Fields, Page, Panel, Row, State, Tabs } from "./adminUi"
import { UserOverviewData } from "./UserOverviewData"

interface ISourceTotals {
	from?: string | number | null
	to?: string | number | null
	truncated?: boolean
}

export interface ICardReconcileReport {
	card?: { uid?: string; last4?: string; currency?: string; [key: string]: unknown }
	balances?: {
		issuer_balance_cents?: number
		issuer_cents?: number
		trx_balance_cents?: number
		trx_cents?: number
		db_balance_cents?: number
		db_mirror_cents?: number
		trx_minus_issuer_cents?: number
		[key: string]: unknown
	}
	issuer?: { transactions?: unknown[]; totals?: ISourceTotals; [key: string]: unknown }
	trx?: { transactions?: unknown[]; totals?: ISourceTotals; [key: string]: unknown }
	local?: { transactions?: unknown[]; [key: string]: unknown }
	gaps?: {
		issuer_only?: unknown[]
		trx_only?: unknown[]
		recorded_not_pushed?: unknown[]
		never_recorded?: unknown[]
		[key: string]: unknown
	}
}

type TransactionTab = "issuer" | "trx" | "local"

const GAP_LABELS = { issuer_only: "Only at the issuer", trx_only: "Only in TRX", recorded_not_pushed: "Recorded, not pushed", never_recorded: "Never recorded" } as const

const firstNumber = (...values: unknown[]) => {
	const value = values.find((item) => typeof item === "number" && Number.isFinite(item))
	return typeof value === "number" ? value : undefined
}

export const SuperAdminCardReconcile = ({ token, card, onClose }: { token: string; card: { uid: string; last4?: string; currency?: string }; onClose: () => void }) => {
	const [report, setReport] = useState<ICardReconcileReport | null>(null)
	const [loading, setLoading] = useState(false)
	const [tab, setTab] = useState<TransactionTab>("issuer")
	const [period, setPeriod] = useState({ from: "", to: "" })
	const [error, setError] = useState("")
	const requestId = useRef(0)
	const periodForm = useRef<HTMLFormElement>(null)

	const load = async (window = period, validate = true) => {
		if (validate && periodForm.current && !periodForm.current.checkValidity()) {
			setError("Enter complete, valid dates or clear the date fields.")
			return
		}
		if (window.from && window.to && window.from > window.to) {
			setError("From date must be on or before To date.")
			return
		}
		const id = ++requestId.current
		setLoading(true)
		setError("")
		setReport(null)
		const response = await systemPost<ICardReconcileReport>(token, "system/cards/reconcile", { card_uid: card.uid, ...(window.from ? { from: window.from } : {}), ...(window.to ? { to: window.to } : {}) })
		if (id !== requestId.current) return
		setLoading(false)
		if (response.error) {
			setError(systemErrorMessage(response.error))
			return toast.error(systemErrorMessage(response.error))
		}
		setReport(response.data)
	}

	useEffect(() => {
		setPeriod({ from: "", to: "" })
		void load({ from: "", to: "" }, false)
		return () => { requestId.current++ }
	}, [card.uid, token]) // eslint-disable-line react-hooks/exhaustive-deps

	const currency = report?.card?.currency || card.currency || "USD"
	const issuerBalance = firstNumber(report?.balances?.issuer_balance_cents, report?.balances?.issuer_cents)
	const trxBalance = firstNumber(report?.balances?.trx_balance_cents, report?.balances?.trx_cents)
	const dbBalance = firstNumber(report?.balances?.db_balance_cents, report?.balances?.db_mirror_cents)
	const difference = firstNumber(report?.balances?.trx_minus_issuer_cents)
	const transactions = report?.[tab]?.transactions ?? []

	const truncated = [report?.issuer?.totals?.truncated && "Issuer", report?.trx?.totals?.truncated && "TRX"].filter(Boolean).join(" and ")
	const balances: Array<[string, number | undefined]> = [["Issuer live", issuerBalance], ["TRX account", trxBalance], ["DB mirror", dbBalance], ["TRX − issuer", difference]]
	const tabLabels: Record<TransactionTab, string> = { issuer: "Issuer transactions", trx: "TRX entries", local: "Local records" }

	return <AdminModal title='Card balance reconciliation' label={`Reconcile card ${card.last4 || card.uid}`} width={1080} onClose={onClose}
		subtitle={`${card.last4 ? `•••• ${card.last4} · ` : ""}${card.uid}`}
		actions={<Button variant='secondarySmall' disabled={loading} onClick={() => void load()}>{loading ? "Checking…" : "Refresh"}</Button>}>
		<Page>
			{loading && !report && <State loading />}
			{report && <Panel title='Balances' hint='Issuer live balance compared with our transaction ledger and the saved card balance.'>
				{(() => {
					const gap = balances.find(([label]) => label === "TRX − issuer")?.[1]
					return typeof gap === "number"
						? <Callout role='status' tone={gap === 0 ? "success" : "warn"} style={{ marginBottom: 16 }}>{gap === 0 ? "Balances match: our ledger equals the issuer." : `Mismatch: our ledger differs from the issuer by ${formatMinorCurrency(gap, currency)}. See the gaps below.`}</Callout>
						: null
				})()}
				<Facts>{balances.map(([label, value]) => <Fact key={label} label={label}><span style={{ fontSize: 15, color: label === "TRX − issuer" && value !== 0 ? T.warn : undefined }}>{formatMinorCurrency(value, currency)}</span></Fact>)}</Facts>
				{truncated && <Callout role='alert' tone='warn' style={{ marginTop: 14 }}>{`Incomplete report: ${truncated} export limit reached. Narrow the period before drawing conclusions.`}</Callout>}
			</Panel>}
			<Panel title='Period' hint={report ? (["issuer", "trx"] as const).map(source => `${source === "issuer" ? "Issuer" : "TRX"}: ${report[source]?.totals ? `${report[source]?.totals?.from ?? "start of history"} → ${report[source]?.totals?.to ?? "now"}` : "full history"}`).join(" · ") : "Leave dates empty for the card’s full history. The To date includes the whole day."}>
				<form ref={periodForm} onSubmit={event => { event.preventDefault(); void load() }} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end" }}>
					<Fields style={{ display: "flex", gap: 12 }}>
						{(["from", "to"] as const).map(name => <label key={name}>{name === "from" ? "From date" : "To date (inclusive)"}<input type='date' value={period[name]} disabled={loading} onChange={event => { setPeriod(current => ({ ...current, [name]: event.target.value })); setError("") }} /></label>)}
					</Fields>
					<Row><Button variant='primarySmall' disabled={loading} onClick={() => void load()}>Apply period</Button><Button variant='secondarySmall' disabled={loading} onClick={() => { setPeriod({ from: "", to: "" }); void load({ from: "", to: "" }, false) }}>All time</Button></Row>
				</form>
				{error && <Callout role='alert' tone='danger' style={{ marginTop: 14 }}>{error}</Callout>}
			</Panel>
			{report && <>
				<Panel title='Gaps' hint='Records that exist in one source but not in the other.'>
					<div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10, alignItems: "start" }}>
						{(["issuer_only", "trx_only", "recorded_not_pushed", "never_recorded"] as const).map((name) => <Disclosure key={name} label={`${GAP_LABELS[name]} (${report.gaps?.[name]?.length ?? 0})`}>
							<UserOverviewData value={report.gaps?.[name] ?? []} />
						</Disclosure>)}
					</div>
				</Panel>
				<Panel title='Transactions'>
					<Tabs role='tablist'>{(["issuer", "trx", "local"] as TransactionTab[]).map((name) => <button key={name} type='button' role='tab' aria-selected={tab === name} onClick={() => setTab(name)}>{tabLabels[name]}</button>)}</Tabs>
					<State empty={transactions.length === 0} emptyText='No rows in this source.' />
					{transactions.length > 0 && <UserOverviewData value={transactions} />}
				</Panel>
			</>}
		</Page>
	</AdminModal>
}
